import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  UserIcon,
  ArrowLeftIcon,
  PencilIcon,
  CalendarIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import Avatar from '../components/Avatar.tsx';
import { userService } from '../services/userService.ts';

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // Fetch user data
  const { data: user, isLoading, error } = useQuery(
    ['user', id],
    () => userService.getUserById(Number(id!)),
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      staleTime: 0, // Sempre considerar os dados como stale
      cacheTime: 0, // Não manter cache
    }
  );

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
      case 'director':
        return 'Administrador';
      case 'user':
      case 'operations':
      case 'finance':
      case 'it':
        return 'Usuário';
      default:
        return 'Usuário';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
      case 'director':
        return 'bg-purple-100 text-purple-800';
      case 'user':
      case 'operations':
      case 'finance':
      case 'it':
        return 'bg-primary-100 text-primary-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Usuário não encontrado</h2>
          <p className="text-gray-600 mb-4">O usuário solicitado não foi encontrado.</p>
          <button
            onClick={() => navigate('/users')}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
          >
            Voltar para Usuários
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary-50 to-primary-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/users')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Usuários
          </button>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Detalhes do Usuário</h1>
              <p className="text-gray-600 mt-2">Visualize as informações do usuário</p>
            </div>
            {hasPermission('users.update') && (
              <button
                onClick={() => navigate(`/users/edit/${user.id}`)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center"
              >
                <PencilIcon className="h-5 w-5 mr-2" />
                Editar Usuário
              </button>
            )}
          </div>
        </div>

        {/* User Info Card */}
        <div className="bg-white/80 backdrop-blur-sm shadow-xl rounded-2xl border border-white/20 overflow-hidden">
          <div className="p-8">
            {/* Profile Section */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-8">
              <div className="flex-shrink-0">
                <Avatar name={user.name} size="xl" src={user.avatar} />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{user.name}</h2>
                <p className="text-gray-600 mb-4 flex items-center justify-center sm:justify-start">
                  <EnvelopeIcon className="h-4 w-4 mr-2" />
                  {user.email}
                </p>
                <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                  <span
                    className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getRoleColor(
                      user.role
                    )}`}
                  >
                    <ShieldCheckIcon className="h-4 w-4 mr-1" />
                    {getRoleLabel(user.role)}
                  </span>
                  <span
                    className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                      user.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {user.active ? (
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 mr-1" />
                    )}
                    {user.active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Account Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Informações da Conta
                </h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Completo
                    </label>
                    <p className="text-gray-900">{user.name}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <p className="text-gray-900">{user.email}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Função
                    </label>
                    <p className="text-gray-900">{getRoleLabel(user.role)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <p className={`font-medium ${user.active ? 'text-green-600' : 'text-red-600'}`}>
                      {user.active ? 'Ativo' : 'Inativo'}
                    </p>
                  </div>
                </div>
              </div>

              {/* System Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-primary-600" />
                  Informações do Sistema
                </h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Criação
                    </label>
                    <p className="text-gray-900">
                      {new Date(user.createdAt).toLocaleDateString('pt-BR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Última Atualização
                    </label>
                    <p className="text-gray-900">
                      {new Date(user.updatedAt).toLocaleDateString('pt-BR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ID do Usuário
                    </label>
                    <p className="text-gray-900 font-mono text-sm">{user.id}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;